<template>
    <div ref="dropdownWidget">
        <button
            ref="dropdownButton"
            v-tippy="t('templateFontStyle')"
            data-dropdown-toggle="dropdown"
            :class="[
                buttonState(editor.can().setFontFamily() && editorSelection.text.length > 0),
                'flex w-48 max-w-48 min-w-48 flex-none items-center justify-between gap-x-2 rounded-sm p-1 text-sm',
            ]"
            :disabled="!editor.can().setFontFamily() || editorSelection.text.length === 0"
            @click="toggleDropdown()"
        >
            <MdiFormatFont
                class="inline h-6 w-6"
                aria-hidden="true"
            />
            <span class="block grow truncate text-left">{{ selected.name }}</span>
            <MdiChevronDown
                class="h-6 w-6 place-self-end text-gray-400"
                aria-hidden="true"
            />
        </button>
        <!-- Dropdown menu -->
        <div
            v-if="dropdownOpen"
            class="fixed top-10 left-0 z-[2002] mt-2 w-48 max-w-48 min-w-48 rounded-md bg-white shadow-sm dark:bg-gray-700"
            :style="{
                top: dropdownPosition.top + 'px',
                left: dropdownPosition.left + 'px',
            }"
        >
            <ul class="divide-y divide-gray-100 py-2 text-sm">
                <li
                    v-for="font in fonts"
                    :key="font.name"
                >
                    <div
                        class="flex cursor-pointer items-center justify-start gap-2 bg-white px-2 py-1 text-gray-600 hover:bg-gray-100"
                        @click="
                            selected = font;
                            toggleDropdown();
                        "
                    >
                        <component
                            :is="font.name === selected.name ? MdiCheck : MdiCircleSmall"
                            :class="[
                                font.name === selected.name ? 'text-green-600' : 'text-gray-400',
                                'h-4 w-4',
                            ]"
                        />
                        {{ font.name }}
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
    import { ref, watch, onMounted, onUnmounted } from 'vue';
    import { searchObjectList } from '../../composables/utils';
    import { buttonState } from '../../composables/useWYSIWYGEditorUtils';

    import {
        MdiChevronDown,
        MdiCheck,
        MdiCircleSmall,
        MdiFormatFont,
    } from 'materialdesignicons-vue3/icons/';

    defineExpose({
        name: 'FontFamilySelect',
    });

    const props = defineProps({
        editor: {
            type: Object,
            required: true,
        },
        fonts: {
            type: Object,
            required: true,
        },
        editorSelection: {
            type: Object,
            required: true,
        },
    });

    const dropdownButton = ref(null);
    const dropdownWidget = ref(null);
    const editor = props.editor;
    const fonts = props.fonts;
    const editorSelection = props.editorSelection;
    const selected = ref(fonts[0]);
    const dropdownOpen = ref(false);
    const dropdownPosition = ref({ top: 0, left: 0 });

    const updateSelected = () => {
        if (editor.isActive('textStyle', { fontFamily: editorSelection.fontFamily })) {
            const findFamily = searchObjectList(fonts, 'family', editorSelection.fontFamily);
            if (findFamily) {
                selected.value = findFamily;
            } else {
                selected.value = fonts[0];
            }
        }
    };

    const toggleDropdown = () => {
        dropdownOpen.value = !dropdownOpen.value;
        if (dropdownOpen.value === true) {
            handleResize();
        }
    };

    const handleResize = () => {
        const buttonRect = dropdownButton.value.getBoundingClientRect();
        const pickerWidth = 280;
        const viewportWidth = window.innerWidth;
        let left = buttonRect.left;
        const top = buttonRect.bottom;
        if (left + pickerWidth > viewportWidth) {
            left = viewportWidth - pickerWidth - 5;
        }
        dropdownPosition.value = { top, left };
    };

    const handleClickOutside = (event) => {
        if (dropdownWidget.value && !dropdownWidget.value.contains(event.target)) {
            dropdownOpen.value = false;
        }
        updateSelected();
    };

    onMounted(() => {
        window.addEventListener('resize', handleResize);
        document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('click', handleClickOutside);
    });

    watch(editorSelection, () => {
        updateSelected();
    });

    watch(selected, () => {
        editor.chain().focus().setFontFamily(selected.value.family).run();
    });
</script>
